import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chat } from "../api/client";

export function useChatConversationList() {
  return useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: () => chat.listConversations(),
    select: (data) => data.conversations,
    refetchInterval: 10_000,
  });
}

export function useChatConversation(id: string | null) {
  return useQuery({
    queryKey: ["chat", "conversation", id],
    queryFn: () => chat.getConversation(id!),
    enabled: !!id,
    refetchInterval: 3_000,
  });
}

export function useDirectChat() {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const clearError = useCallback(() => setError(null), []);

  const startConversation = useCallback(async () => {
    const conv = await chat.createConversation();
    setConversationId(conv.id);
    queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    return conv;
  }, [queryClient]);

  const sendMessage = useCallback(async (content: string) => {
    // Set sending FIRST, before any async work
    setSending(true);
    setError(null);

    try {
      let targetId = conversationId;
      if (!targetId) {
        const conv = await startConversation();
        targetId = conv.id;
      }

      const response = await chat.sendMessage(targetId, content);
      queryClient.invalidateQueries({ queryKey: ["chat", "conversation", targetId] });
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }, [conversationId, startConversation, queryClient]);

  const deleteConversation = useCallback(async (id: string) => {
    try {
      await chat.deleteConversation(id);
      if (conversationId === id) {
        setConversationId(null);
      }
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete conversation");
    }
  }, [conversationId, queryClient]);

  const renameConversation = useCallback(async (id: string, title: string) => {
    await chat.updateTitle(id, title);
    queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    queryClient.invalidateQueries({ queryKey: ["chat", "conversation", id] });
  }, [queryClient]);

  return {
    conversationId,
    setConversationId,
    sending,
    error,
    clearError,
    sendMessage,
    startConversation,
    deleteConversation,
    renameConversation,
  };
}
